import { Box, Link, Pagination, Stack, Typography } from '@mui/material';
import { APP_PAGE_SIZE_DEFAULT } from 'app/appConstants';
import { AppGlobalClassKeys } from 'app/appGlobalStyles';
import { MprcContainer, MprcRequestErrorReport, MprcSpinnerIndicator } from 'components';
import { MprcFilterCountInfo } from 'components/filter';
import { MprcFilterNoRecords } from 'components/filter/MprcFilterNoRecords';
import { MprcMarkdown } from 'components/MprcMarkdown';
import { MprcSearchTextField } from 'components/MprcSearchTextField';
import { GlobalSearchAttachDto, PublicationAttachType, TrainingComponentSearchDto, TrainingComponentSearchDtoPaginatedList } from 'generated/CoreApi_v1';
import { enqueueSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { buildHeadLine, translateFileExtension } from 'views/general/GlobalSearch/components/SearchRenderPublication';
import { publishVideoSearchEvent } from 'views/knowledge/components/card/CardEvents';
import { useEnrollmentViewContext } from '..';
import { useCourseEnrollmentViewContext } from './CourseEnrollmentView';

/////////////////////////////////////////////////////////////////////////////////////

export const CourseEnrollmentViewSearch: React.FC = () => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");

    const { enrollmentId, enrollment: { isPreview, classroomId } } = useEnrollmentViewContext();
    const { currentComponent, setCurrentComponentId } = useCourseEnrollmentViewContext();

    const [search, setSearch] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [trainingResult, setTrainingResult] = useState<TrainingComponentSearchDtoPaginatedList>();
    const [pageNumber, setPageNumber] = useState<number>(1);

    /////////////////////////////////////////////////////////////////////////////////////

    ///
    async function loadPublications(searchText: string) {

        if (searchText.length == 0) {
            setTrainingResult(undefined);
            return;
        }

        setLoading(true);
        api.v1.enrollment.searchTrainings({
            enrollmentId: !isPreview ? enrollmentId : null,
            classroomId: !isPreview ? null : classroomId,
            searchText,
            pageNumber: pageNumber,
            pageSize: APP_PAGE_SIZE_DEFAULT
        }).then(response => {
            setTrainingResult(response.data);
        })
            .catch(e => setViewError(e))
            .finally(() => setLoading(false));
    }

    ///
    const handleSelectPublication = (item: TrainingComponentSearchDto, attach?: GlobalSearchAttachDto) => {
        if (!item.accessEnabled) {
            enqueueSnackbar(t("Student.EnrollmentView.Search.ContentDisabledMessage"), { variant: "warning" });
        } else {

            setCurrentComponentId(item.componentId);
            if (attach) publishVideoSearchEvent({ attachId: attach.attachId, searchText: search });
            else {
                if (item.componentId == currentComponent?.componentId) {
                    enqueueSnackbar(t("Student.EnrollmentView.Search.IsCurrentModuleMessage"), { variant: "info" });
                }
            }

        }
    };

    /////////////////////////////////////////////////////////////////////////////////////

    return <>
        <Box sx={{ mb: 1 }}>
            <MprcSearchTextField
                autoFocus
                initialValue={search}
                onConfirm={(value) => { setSearch(value); loadPublications(value); }}
                placeholder={t("Student.EnrollmentView.Search.Placeholder")}
            />
        </Box>
        <MprcRequestErrorReport error={viewError} />
        {loading
            ? <MprcSpinnerIndicator />
            :
            trainingResult == undefined ?
                <Typography variant="caption" color="muted.light">
                    <Trans i18nKey="Student.EnrollmentView.Search.Hint" t={t} />
                </Typography>
                :
                trainingResult.items.length == 0
                    ? <MprcFilterNoRecords
                        message={t("Student.EnrollmentView.Search.NoRecordsFoundText")}
                        filtered={search.length > 0}
                        onResetFilter={() => setSearch("")}
                    />
                    : <>
                        <MprcContainer>
                            <MprcFilterCountInfo
                                text={t("Student.EnrollmentView.Search.RecordsFoundText", { count: trainingResult.totalRows })}
                            />
                        </MprcContainer>
                        {trainingResult.items.map((item, index) => {
                            const headline = buildHeadLine(item.headline ?? item.summary);
                            return (
                                <MprcContainer key={index}>
                                    <Link onClick={() => handleSelectPublication(item)}>
                                        <Typography variant="h5" color="highlight.main">
                                            {item.title}
                                        </Typography>
                                    </Link>
                                    {
                                        headline &&
                                        <Typography variant="caption" component="div">
                                            <MprcMarkdown>{headline}</MprcMarkdown>
                                        </Typography>
                                    }
                                    {
                                        item.attachs.length > 0 &&
                                        <Stack sx={{ ml: 3 }} gap={0.25}>
                                            {item.attachs.map(attach =>
                                                <Stack direction="row" key={attach.headline} alignItems="flex-start" component="p" sx={{ m: 1 }}>
                                                    <Typography
                                                        variant="captionHeader"
                                                        color="primary.main"
                                                        component="span"
                                                        className={AppGlobalClassKeys.clickable}
                                                        onClick={
                                                            () => handleSelectPublication(item, attach)
                                                        }
                                                    >
                                                        {attach.attachType == PublicationAttachType.Document && attach.filename
                                                            ? t(translateFileExtension(attach.filename))
                                                            : attach.attachTypeName
                                                        }
                                                        <span>:&nbsp;</span>
                                                    </Typography>
                                                    <Typography variant="caption" component="span">
                                                        <MprcMarkdown>{attach.headline}</MprcMarkdown>
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </Stack>
                                    }
                                </MprcContainer>
                            );
                        }
                        )}
                        {
                            trainingResult.totalPages > 1 &&
                            <Pagination
                                shape="rounded"
                                color="primary"
                                page={trainingResult.pageNumber}
                                count={trainingResult.totalPages}
                                onChange={
                                    (e, pg) => {
                                        if (pageNumber != pg) {
                                            setPageNumber(pg);
                                            setLoading(true);
                                        }
                                    }
                                }
                            />
                        }
                    </>
        }
    </>;
};
